import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import BackLink from "@/components/BackLink";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, FileDown } from "lucide-react";
import EmailDialog from "@/components/EmailDialog";
import { inr, formatDate, amountInWords } from "@/lib/utils2";

function firstOfMonth() {
  const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10);
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function TransporterSettlement() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [from, setFrom] = usePersistedState(`settl:${id}:from`, () => sp.get("from") || firstOfMonth());
  const [to, setTo] = usePersistedState(`settl:${id}:to`, () => sp.get("to") || todayISO());
  const [trans, setTrans] = useState(null);
  const [detail, setDetail] = useState({ trucks: [], on_account: 0 });
  const [payments, setPayments] = useState([]);
  const [company, setCompany] = useState({});

  useEffect(() => { (async () => {
    const [t, td, pp, s] = await Promise.all([
      api.get("/transporters"),
      api.get(`/transporter/${id}/trucks-detail`),
      api.get("/payments-made", { params: { transporter_id: id } }),
      api.get("/settings"),
    ]);
    setTrans(t.data.find((x) => x.id === id));
    setDetail(td.data);
    setPayments(pp.data);
    setCompany(s.data || {});
  })(); }, [id]);

  const inPeriod = (d) => (!from || d >= from) && (!to || d <= to);
  const trucksInPeriod = useMemo(() => detail.trucks.filter((t) => inPeriod(t.entry_date)), [detail.trucks, from, to]);
  const paymentsInPeriod = useMemo(() => payments.filter((p) => inPeriod(p.date)), [payments, from, to]);

  // Opening balance = balances BEFORE the period start
  const opening = useMemo(() => {
    const beforeTrucks = detail.trucks.filter((t) => t.entry_date < from);
    const beforePays = payments.filter((p) => p.date < from);
    const owed = beforeTrucks.reduce((s, t) => s + (t.total_owed || 0), 0);
    const paid = beforePays.reduce((s, p) => s + p.amount, 0);
    return owed - paid;
  }, [detail.trucks, payments, from]);

  const periodCharges = trucksInPeriod.reduce((s, t) => s + (t.total_owed || 0), 0);
  const periodPaid = paymentsInPeriod.reduce((s, p) => s + p.amount, 0);
  const closing = opening + periodCharges - periodPaid;

  if (!trans) return <div className="p-10 text-brand-muted">Loading…</div>;

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-4xl mx-auto" data-testid="transporter-settlement-page">
      <div className="no-print flex items-center justify-between mb-5">
        <BackLink to={`/ledger/transporter/${id}`} label="Back" testId="transporter-settlement-back" />
        <div className="flex items-center gap-2">
        <Button onClick={async () => {
          try {
            const res = await api.get(`/transporter/${id}/settlement-pdf`, { params: { date_from: from, date_to: to }, responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `Settlement-${trans.name.replace(/\s+/g,"_")}-${from}_to_${to}.pdf`; a.click();
            URL.revokeObjectURL(url);
          } catch (e) {}
        }} variant="outline" data-testid="download-trn-settlement-pdf-btn" className="rounded-sm border-brand-ink">
          <FileDown className="h-4 w-4 mr-1.5" /> Download PDF
        </Button>
        <EmailDialog
          endpoint={`/transporter/${id}/settlement-email`}
          queryParams={{ date_from: from, date_to: to }}
          defaultTo={trans?.email || ""}
          testIdPrefix="email-settlement"
          buttonLabel="Email Settlement"
        />
        <Button onClick={() => window.print()} data-testid="print-trn-settlement-btn" className="rounded-sm bg-brand-forest text-white">
          <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
        </Button>
        </div>
      </div>

      <Card className="no-print rounded-sm border-brand-line shadow-none p-5 mb-5">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>From</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label>To</Label><Input type="date" className="mt-1.5 h-10 rounded-sm bg-white" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        </div>
      </Card>

      <div className="print-area bg-white border border-brand-line rounded-sm">
        <div className="px-8 pt-7 pb-5 border-b-2 border-brand-ink">
          <div className="text-center text-[11px] uppercase tracking-[0.3em] text-brand-muted mb-2">Transporter Settlement Sheet</div>
          <div className="flex justify-between items-start gap-6">
            <div className="flex items-start gap-4">
              {company.logo_base64 && <img src={company.logo_base64} alt="" className="h-14 w-14 object-contain border border-brand-line rounded-sm" />}
              <div>
                <div className="font-serif text-2xl text-brand-ink tracking-tight">{company.company_name}</div>
                <div className="text-[11px] text-brand-muted mt-1">{company.address}<br/>{company.city}<br/>GSTIN: {company.gstin} · PAN: {company.pan}</div>
              </div>
            </div>
            <div className="text-xs text-right">
              <div className="uppercase tracking-[0.15em] text-brand-muted text-[10px]">Period</div>
              <div className="font-medium">{formatDate(from)} → {formatDate(to)}</div>
              <div className="mt-2 uppercase tracking-[0.15em] text-brand-muted text-[10px]">Issued On</div>
              <div className="font-medium">{formatDate(todayISO())}</div>
            </div>
          </div>
        </div>

        <div className="px-8 py-4 border-b border-brand-line text-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">Transporter</div>
          <div className="font-serif text-xl text-brand-ink mt-1">{trans.name}</div>
          {trans.phone && <div className="text-xs text-brand-muted">Phone: {trans.phone}</div>}
          {trans.address && <div className="text-xs text-brand-muted">{trans.address}</div>}
        </div>

        <div className="px-8 py-5">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-brand-ink text-[10px] uppercase tracking-[0.12em]">
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Vehicle</th>
                <th className="text-left py-2">Material</th>
                <th className="text-right py-2">Wgt</th>
                <th className="text-right py-2">Transport ₹</th>
                <th className="text-right py-2">Material ₹</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2">Paid</th>
                <th className="text-right py-2">Balance</th>
              </tr>
              <tr className="border-b border-brand-line bg-secondary/40">
                <td colSpan={6} className="py-2 text-[10px] uppercase tracking-[0.15em] text-brand-muted">Opening Balance (prior to {formatDate(from)})</td>
                <td colSpan={2} className="py-2"></td>
                <td className="py-2 text-right tabular-nums font-medium">{inr(opening)}</td>
              </tr>
            </thead>
            <tbody>
              {trucksInPeriod.length === 0 && (
                <tr><td colSpan={9} className="py-3 text-center text-brand-muted">No trips in this period</td></tr>
              )}
              {trucksInPeriod.map((t) => (
                <tr key={t.truck_id} className="border-b border-brand-line align-top">
                  <td className="py-2 whitespace-nowrap">{formatDate(t.entry_date)}</td>
                  <td className="py-2 font-medium">{t.vehicle_number}</td>
                  <td className="py-2 text-brand-muted">{t.material_name || "—"}</td>
                  <td className="py-2 text-right tabular-nums">{t.weight}</td>
                  <td className="py-2 text-right tabular-nums">{inr(t.transport_cost)}</td>
                  <td className="py-2 text-right tabular-nums">{inr(t.material_cost)}</td>
                  <td className="py-2 text-right tabular-nums font-medium">{inr(t.total_owed)}</td>
                  <td className="py-2 text-right tabular-nums text-brand-forest">{inr(t.paid)}</td>
                  <td className="py-2 text-right tabular-nums">{inr(t.balance)}</td>
                </tr>
              ))}
              <tr className="border-b border-brand-line bg-secondary/40 font-semibold">
                <td colSpan={4} className="py-2 text-[10px] uppercase tracking-[0.15em]">Period Charges</td>
                <td colSpan={2} className="py-2"></td>
                <td className="py-2 text-right tabular-nums">{inr(periodCharges)}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>

          {paymentsInPeriod.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted mb-2">Payments Made in this period</div>
              <table className="w-full text-xs">
                <thead className="border-b border-brand-ink">
                  <tr className="text-[10px] uppercase tracking-[0.12em]">
                    <th className="text-left py-1.5">Date</th>
                    <th className="text-left py-1.5">Mode</th>
                    <th className="text-left py-1.5">Particulars</th>
                    <th className="text-right py-1.5">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentsInPeriod.map((p) => (
                    <tr key={p.id} className="border-b border-brand-line">
                      <td className="py-1.5">{formatDate(p.date)}</td>
                      <td className="py-1.5">{p.mode}</td>
                      <td className="py-1.5 text-brand-muted">{p.particulars || "—"}</td>
                      <td className="py-1.5 text-right tabular-nums">{inr(p.amount)}</td>
                    </tr>
                  ))}
                  <tr className="bg-secondary/40 font-semibold">
                    <td colSpan={3} className="py-2 text-[10px] uppercase tracking-[0.15em]">Total Paid in period</td>
                    <td className="py-2 text-right tabular-nums">{inr(periodPaid)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-5 border-t-2 border-brand-ink pt-4 grid grid-cols-2 gap-6 text-sm">
            <div className="text-[11px] text-brand-muted">
              <div className="uppercase tracking-[0.15em] text-[10px] mb-1">Amount in words</div>
              <div className="font-medium text-brand-ink uppercase">{amountInWords(Math.abs(closing))}{closing < 0 ? " (CR.)" : ""}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Closing Balance Due</div>
              <div className={`font-serif text-3xl tabular-nums ${closing > 0 ? "text-brand-terracotta" : "text-brand-forest"}`}>{inr(closing)}</div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-6 text-[11px]">
            <div>
              <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">Acknowledged by Transporter</div>
              <div className="h-16"></div>
              <div className="border-t border-brand-ink pt-1.5 inline-block min-w-[200px]">Signature & Date</div>
            </div>
            <div className="text-right">
              <div className="text-brand-muted uppercase tracking-[0.15em] text-[10px] mb-1">For {company.company_name}</div>
              <div className="h-16"></div>
              <div className="border-t border-brand-ink pt-1.5 inline-block min-w-[200px]">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
